<script module lang="ts">
  import type { IconName } from "./icons.ts";

  interface Segment<T extends string = string> {
    readonly value: T;
    readonly label: string;
    readonly note?: string;
    readonly icon?: IconName;
    readonly disabled?: boolean;
    readonly locked?: boolean;
  }
</script>

<script lang="ts" generics="T extends string">
  import { choiceKeys } from "./choice-keys.ts";
  import Icon from "./Icon.svelte";
  import type { Snippet } from "svelte";
  import {
    CHIP_FRAME,
    CONTROL_HEIGHT,
    CHIP_OFF,
    CHIP_ON,
    FOCUS_RING,
    PRESS,
    SWITCH_OFF,
    SWITCH_ON,
    SWITCH_PILL,
    SWITCH_TRACK,
  } from "./recipes.ts";

  let {
    options,
    value,
    onchange,
    ariaLabel,
    title,
    testId,
    look = "control",
    full = false,
    disabled = false,
    mark,
  }: {
    options: readonly Segment<T>[];
    value: T | null;
    onchange: (value: T) => void;
    ariaLabel: string;
    title?: string | undefined;
    testId?: string;
    look?: "control" | "pill" | "chips" | "framed" | "field";
    full?: boolean;
    disabled?: boolean;
    mark?: Snippet<[T]> | undefined;
  } = $props();

  const LOOKS = {
    control: {
      track: "inline-flex shrink-0 items-stretch gap-0.5 rounded-control bg-surface-2 p-0.5",
      segment: "rounded-segment px-2.5 text-sm font-medium",
      on: "bg-surface-0 text-ink-1 shadow-sm",
      off: "text-ink-2 hover:enabled:text-ink-1",
    },
    pill: {
      track: SWITCH_TRACK,
      segment: SWITCH_PILL,
      on: SWITCH_ON,
      off: SWITCH_OFF,
    },
    chips: {
      track: "flex flex-wrap gap-1",
      segment: CHIP_FRAME,
      on: CHIP_ON,
      off: CHIP_OFF,
    },
    framed: {
      track: `flex ${CONTROL_HEIGHT} rounded-control border border-edge bg-surface-0 p-0.5`,
      segment: "flex-1 rounded-segment px-2.5 text-sm font-medium",
      on: "bg-surface-2 text-ink-1",
      off: "text-ink-2 hover:enabled:text-ink-1",
    },
    field: {
      track: "flex h-6 w-full min-w-0 gap-px rounded-control bg-surface-2 p-px pointer-coarse:h-8",
      segment: "grid min-w-0 flex-1 place-items-center rounded-segment px-1.5 text-xs font-medium",
      on: "bg-surface-1 text-ink-1 ring-1 ring-edge ring-inset",
      off: "text-ink-3 hover:enabled:text-ink-1",
    },
  } as const;

  const style = $derived(LOOKS[look]);

  const entry = $derived(
    options.some((option) => option.value === value)
      ? value
      : options.find((option) => option.disabled !== true)?.value,
  );
</script>

<div
  {@attach choiceKeys("[role='radio']", { picks: true })}
  role="radiogroup"
  aria-label={ariaLabel}
  {title}
  data-testid={testId}
  class={[style.track, look === "control" && "min-h-7.5 pointer-coarse:min-h-10", full && "w-full"]}
>
  {#each options as option (option.value)}
    {@const on = option.value === value}
    <button
      type="button"
      role="radio"
      aria-checked={on}
      tabindex={option.value === entry ? 0 : -1}
      disabled={disabled || option.disabled === true}
      aria-label={option.icon === undefined ? undefined : option.label}
      title={option.icon === undefined ? undefined : option.label}
      data-testid={testId === undefined ? undefined : `${testId}-${option.value}`}
      onclick={() => onchange(option.value)}
      class={[
        `${PRESS} ${FOCUS_RING} disabled:cursor-not-allowed disabled:opacity-40`,
        style.segment,
        look === "control" && "py-0.5",
        full && "flex-1 justify-center",
        on ? style.on : style.off,
        option.icon !== undefined && "grid place-items-center",
      ]}
    >
      {#if option.icon !== undefined}
        <Icon name={option.icon} size={look === "field" ? 14 : 15} />
      {:else if look === "field"}
        <span class="truncate">{option.label}</span>
      {:else}
        {#if mark !== undefined}<span class="mr-1.5 inline-flex align-middle"
            >{@render mark(option.value)}</span
          >{/if}{option.label}{#if option.locked}<span
            class="ml-1 inline-flex align-middle text-ink-3"
            ><Icon name="planLocked" size={12} /></span
          >{/if}{#if option.note !== undefined}<span class="ml-1 font-normal opacity-70"
            >{option.note}</span
          >{/if}
      {/if}
    </button>
  {/each}
</div>
